/* eslint-disable camelcase */
import { ReactNode, useEffect, useState } from 'react';
import { Alert, Box, Grid, Link, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import { getCustomerById } from 'src/shared/services/customer';
import { CustomerSettings } from 'src/modules/admin/customersettings/models/customerSettings';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

// Read-only-fält (etikett + värde) för kund-panelen. whiteSpace: pre-line → radbrytningar i adress visas.
const ReadOnlyField = ({ label, value, md = 6 }: { label: string; value?: ReactNode; md?: number }) => (
  <Grid item xs={12} md={md}>
    <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant='body2' component='div' sx={{ fontWeight: 500, whiteSpace: 'pre-line' }}>
      {value || '—'}
    </Typography>
  </Grid>
);

export const SendingWizardStep1Details = () => {
  const { setValue, getValues, control } = useFormContext();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const ownerId = useWatch({ control, name: 'owner_id' }) as number | undefined;
  const [customer, setCustomer] = useState<CustomerSettings | null>(null);

  // Autofyll beställarens namn + e-post från inloggad användare (skriv inte över om fälten redan är ifyllda).
  useEffect(() => {
    if (!currentUser) return;
    const name = [currentUser.firstName, currentUser.lastName].filter((x) => !!x).join(' ');
    if (name && !getValues('createdByName')) setValue('createdByName', name);
    if (currentUser.email && !getValues('createdByEmail')) setValue('createdByEmail', currentUser.email);
  }, [currentUser, getValues, setValue]);

  // När fakturagrupp valts: slå upp "kund för fakturering". Fakturagruppens customerNr = member_id i
  // vårt eget medlemsregister (custName 'fsupport'). getCustomerById hämtar den kunden (backend hårdkodar fsupport).
  useEffect(() => {
    if (!ownerId || ownerId <= 0) {
      setCustomer(null);
      return;
    }

    (async () => {
      try {
        const groupRes = await getInvoiceGroupById(ownerId);
        const customerNr = groupRes.data?.customerNr;

        if (!customerNr || Number.isNaN(Number(customerNr))) {
          if (isMounted()) setCustomer(null);
          return;
        }

        const custRes = await getCustomerById(Number(customerNr));
        if (isMounted()) setCustomer(custRes.data ?? null);
      } catch (e) {
        ConsoleHelper.log(e);
        if (isMounted()) setCustomer(null);
      }
    })();
  }, [ownerId, isMounted]);

  const customerName = customer ? [customer.firstName, customer.lastName].filter((x) => !!x).join(' ') : '';

  const addressText = customer
    ? [
        customer.coAddress ? `c/o ${customer.coAddress}` : '',
        customer.address ?? '',
        [customer.postNr, customer.postAddress].filter((x) => !!x).join(' '),
        customer.country ?? '',
      ]
        .filter((x) => !!x && x.trim() !== '')
        .join('\n')
    : '';

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
          Vem beställer utskicket?
        </Typography>
      </Grid>

      {/* Vänsterkolumn: fakturagrupp + beställarens namn/e-post staplade under varandra. */}
      <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <InvoiceGroups
            autoSelect
            callback={(ownId) => {
              setValue('owner_id', ownId);
            }}
            placeholder='Välj fakturagrupp'
            emptyLabel='Välj fakturagrupp'
            ownerId={getValues('owner_id')}
          />

          <Box>
            <Typography variant='body1' sx={{ fontWeight: 600, mb: 0.5 }}>
              Beställare
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Ange ditt namn och e-postadress. Ditt namn används som referens på beställningen. Bekräftelse på beställningen skickas till din
              e-postadress.
            </Typography>
          </Box>

          <ControlledTextField name='createdByName' required label='Beställare, ditt namn' inputProps={{ maxLength: 100 }} />
          <ControlledTextField name='createdByEmail' required label='Beställare, din e-post' inputProps={{ maxLength: 320 }} />
        </Box>
      </Grid>

      {/* Högerkolumn: kundinformation i systemets blå info-ruta. */}
      {customer && (
        <Grid item xs={12} md={6}>
          <Alert severity='info' icon={false} sx={{ height: '100%' }}>
            <Grid container spacing={2}>
              <ReadOnlyField label='Kund' value={customerName} md={12} />
              <ReadOnlyField label='Ert kundnummer hos Föreningssupport' value={customer.custom1} md={12} />
              <ReadOnlyField label='Adress' value={addressText} md={12} />
              <ReadOnlyField label='E-postadresser för godkännande av utskick' value={customer.custom3} md={12} />
              <Grid item xs={12}>
                <Typography variant='caption' color='text.secondary'>
                  Stämmer inte uppgifterna? Kontakta oss på <Link href='mailto:support@foreningssupport.se'>support@foreningssupport.se</Link>
                </Typography>
              </Grid>
            </Grid>
          </Alert>
        </Grid>
      )}
    </GridWrapper>
  );
};
